import { 
  getNotificationList, 
  getTodoList, 
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  handleTodo,
  deleteNotification,
  batchDeleteNotifications,
  getNotificationStats
} from '@/api/notification'
import NotificationSwitcher from '@/views/notification/components/notification-switcher.js'

const DEFAULT_BASE_URL = 'auto'

const getEnvNotificationBaseUrl = () => {
  if (typeof process !== 'undefined' && process.env && process.env.VUE_APP_NOTIFICATION_WS_BASE) {
    return process.env.VUE_APP_NOTIFICATION_WS_BASE
  }
  return null
}

const resolveNotificationBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const storageOverride = (window.localStorage && window.localStorage.getItem) ? window.localStorage.getItem('NOTIFICATION_WS_BASE') : null
    const runtimeOverride = window.__NOTIFICATION_WS_BASE__ || storageOverride
    if (runtimeOverride) {
      return runtimeOverride
    }
  }

  const envBase = getEnvNotificationBaseUrl()
  if (envBase) {
    return envBase
  }

  return DEFAULT_BASE_URL
}

const resolveUserId = (rootState, explicitUserId) => {
  if (explicitUserId) {
    return explicitUserId
  }

  const storeUserId = rootState.user?.userId || rootState.user?.id
  if (storeUserId) {
    return storeUserId
  }

  if (typeof window !== 'undefined') {
    try {
      const storedUserInfo = window.sessionStorage.getItem('userInfo')
      if (storedUserInfo) {
        const parsed = JSON.parse(storedUserInfo)
        if (parsed?.userId || parsed?.id) {
          return parsed.userId || parsed.id
        }
      }
    } catch (error) {
      console.warn('解析 sessionStorage userInfo 失败:', error)
    }

    const sessionUid = window.sessionStorage.getItem('_uid')
    if (sessionUid) {
      return sessionUid
    }
  }

  return null
}

const state = {
  // 通知列表
  notifications: [],
  // 待办列表
  todos: [],
  // 未读数量
  unreadCount: 0,
  // 加载状态
  loading: false,
  // 分页信息
  pagination: {
    page: 1,
    size: 10,
    total: 0
  },
  // WebSocket连接状态
  websocketConnected: false,
  // WebSocket管理器实例
  websocketManager: null,
  // 通知切换器实例
  notificationSwitcher: null,
  // 当前连接的用户
  currentUserId: null,
  // 是否已完成初始化
  isInitialized: false,
  // 当前使用的连接地址
  websocketBaseUrl: resolveNotificationBaseUrl(),
  // 通知统计
  stats: {
    total: 0,
    unread: 0,
    today: 0,
    thisWeek: 0
  },
  // 搜索关键词
  searchKeyword: '',
  // 过滤条件
  filters: {
    type: 'all', // all, automation, approval, system, error
    status: 'all', // all, read, unread
    dateRange: null
  }
}

const normalizeNotification = (raw) => {
  if (!raw || typeof raw !== 'object') return raw
  
  // 优先使用原始 id 字段，然后是 notificationId、messageId 等
  const serverId = raw.id || raw.notificationId || raw.messageId || raw.uid || raw.uuid
  
  // 如果没有找到任何ID，生成一个临时ID
  const finalId = serverId || raw.tempId || `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  
  // 规范化 type 字段：优先使用 type，如果没有则使用 notificationType
  const normalizedType = raw.type || raw.notificationType || null
  
  // 确保 read 字段有默认值（未定义时默认为 false，表示未读）
  const readStatus = raw.read !== undefined ? raw.read : false
  
  return {
    ...raw,
    id: finalId,
    type: normalizedType, // 统一使用 type 字段
    notificationType: normalizedType, // 保留 notificationType 以兼容旧代码
    read: readStatus, // 确保 read 字段有值
    __serverId: serverId || null // 保存原始服务器ID（如果存在）
  }
}

const mutations = {
  SET_NOTIFICATIONS(state, { data, total }) {
    state.notifications = Array.isArray(data) ? data.map(item => normalizeNotification(item)) : []
    state.pagination.total = total
  },

  SET_TODOS(state, { data }) {
    state.todos = Array.isArray(data) ? data : []
  },

  APPEND_NOTIFICATIONS(state, newNotifications) {
    if (Array.isArray(newNotifications)) {
      state.notifications = [...state.notifications, ...newNotifications]
    }
  },

  APPEND_TODOS(state, newTodos) {
    if (Array.isArray(newTodos)) {
      state.todos = [...state.todos, ...newTodos]
    }
  },
  
  SET_UNREAD_COUNT(state, count) {
    state.unreadCount = count
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  ADD_NOTIFICATION(state, notification) {
    if (!Array.isArray(state.notifications)) {
      state.notifications = []
    }
    
    // 统一规范化ID，优先使用后端真实ID
    notification = normalizeNotification(notification)
    
    // 检查是否已存在相同ID的通知
    const existingIndex = state.notifications.findIndex(n => n.id === notification.id)
    if (existingIndex === -1) {
      // 新通知，添加到列表顶部
      state.notifications.unshift(notification)
      if (!notification.read) {
        state.unreadCount++
      }
    } else {
      // 通知已存在，更新它（保留原有的已读状态，除非新通知明确标记为已读）
      const existingNotification = state.notifications[existingIndex]
      const wasUnread = !existingNotification.read
      const isUnread = !notification.read
      
      // 更新通知内容，但保留已读状态（如果原通知是已读的）
      state.notifications[existingIndex] = {
        ...notification,
        read: existingNotification.read || notification.read // 如果原来已读或新通知标记为已读，则为已读
      }
      
      // 更新未读计数
      if (wasUnread && !state.notifications[existingIndex].read) {
        // 原来是未读，现在还是未读，计数不变
      } else if (wasUnread && state.notifications[existingIndex].read) {
        // 原来是未读，现在是已读，减少计数
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      } else if (!wasUnread && isUnread) {
        // 原来是已读，现在是未读，增加计数
        state.unreadCount++
      }
    }
  },
  
  MARK_AS_READ(state, notificationId) {
    const notification = state.notifications.find(n => n.id === notificationId)
    if (notification && !notification.read) {
      notification.read = true
      state.unreadCount = Math.max(0, state.unreadCount - 1)
    }
  },
  
  MARK_ALL_AS_READ(state) {
    // 使用map创建新数组，确保Vue响应式更新
    state.notifications = state.notifications.map(notification => ({
      ...notification,
      read: true
    }))
    state.unreadCount = 0
  },
  
  REMOVE_NOTIFICATION(state, notificationId) {
    const index = state.notifications.findIndex(n => n.id === notificationId)
    if (index > -1) {
      const notification = state.notifications[index]
      if (!notification.read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
      state.notifications.splice(index, 1)
    }
  },
  
  BATCH_REMOVE_NOTIFICATIONS(state, notificationIds) {
    notificationIds.forEach(id => {
      const index = state.notifications.findIndex(n => n.id === id)
      if (index > -1) {
        const notification = state.notifications[index]
        if (!notification.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
        state.notifications.splice(index, 1)
      }
    })
  },
  
  SET_WEBSOCKET_CONNECTED(state, connected) {
    state.websocketConnected = connected
  },

  SET_WEBSOCKET_MANAGER(state, manager) {
    state.websocketManager = manager
  },

  SET_NOTIFICATION_SWITCHER(state, switcher) {
    state.notificationSwitcher = switcher
  },

  SET_CURRENT_USER_ID(state, userId) {
    state.currentUserId = userId
  },

  SET_INITIALIZED(state, initialized) {
    state.isInitialized = initialized
  },

  SET_WEBSOCKET_BASE_URL(state, baseUrl) {
    state.websocketBaseUrl = baseUrl
  },

  SET_STATS(state, stats) {
    state.stats = stats
  },
  
  SET_SEARCH_KEYWORD(state, keyword) {
    state.searchKeyword = keyword
  },
  
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  
  UPDATE_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination }
  },
  
  CLEAR_NOTIFICATIONS(state) {
    state.notifications = []
    state.unreadCount = 0
  }
}

const actions = {
  // 初始化通知中心
  async initNotificationCenter({ state, commit, dispatch, rootState }, { userId, baseUrl, force = false } = {}) {
    try {
      const resolvedUserId = resolveUserId(rootState, userId)

      if (!resolvedUserId) {
        console.warn('初始化通知中心失败: 无法确定用户ID')
        return
      }

      let switcher = state.notificationSwitcher
      const resolvedBaseUrl = baseUrl || resolveNotificationBaseUrl()
      const alreadyConnected = !force && switcher && state.websocketConnected && state.currentUserId === resolvedUserId && state.websocketBaseUrl === resolvedBaseUrl

      if (alreadyConnected) {
        return
      }

      const shouldCreateSwitcher = !switcher || state.currentUserId !== resolvedUserId || state.websocketBaseUrl !== resolvedBaseUrl

      if (state.websocketBaseUrl !== resolvedBaseUrl) {
        commit('SET_WEBSOCKET_BASE_URL', resolvedBaseUrl)
      }

      if (shouldCreateSwitcher) {
        if (switcher) {
          try {
            switcher.disconnect()
          } catch (error) {
            console.warn('断开旧的通知连接失败:', error)
          }
        }

        switcher = new NotificationSwitcher({
          baseUrl: resolvedBaseUrl,
          defaultMethod: 'websocket',
          userId: resolvedUserId,
          heartbeatPayload: 'ping',
          heartbeatInterval: 30000,  // 30秒发送一次心跳，减少服务器压力
          heartbeatTimeout: 90000,   // 90秒没收到心跳才断开，给网络波动更多容错时间
          maxReconnectDelay: 30000,
          baseReconnectDelay: 1000
        })

        commit('SET_NOTIFICATION_SWITCHER', switcher)
        commit('SET_CURRENT_USER_ID', resolvedUserId)
        commit('SET_INITIALIZED', false)
        switcher.__listenersBound = false
      } else if (force && switcher) {
        switcher.disconnect()
      }

      if (switcher && !switcher.__listenersBound) {
        switcher.on('connected', () => {
          commit('SET_WEBSOCKET_CONNECTED', true)
          commit('SET_WEBSOCKET_MANAGER', switcher.webSocketManager || null)
        })

        switcher.on('disconnected', () => {
          commit('SET_WEBSOCKET_CONNECTED', false)
        })

        switcher.on('notification', (payload) => {
          // 处理 WebSocket 消息结构
          // 可能的结构：
          // 1. { id: "...", payload: {...} } - 外层有 id
          // 2. { data: { id: "...", ... } } - data 里有 id
          // 3. { id: "...", ... } - 直接是通知对象
          let data = payload?.data || payload
          
          // 如果外层有 id，需要将外层的 id 合并到内层（双重保护）
          if (payload?.id && !data?.id && !data?.notificationId) {
            // 确保 data 是对象，不是数组
            if (data && typeof data === 'object' && !Array.isArray(data)) {
              data = { ...data, id: payload.id }
            }
          }
          
          // 确保 data 是对象
          if (data && typeof data === 'object' && !Array.isArray(data)) {
            // 先规范化通知，生成ID（这会优先使用已有的 id 字段）
            const normalizedData = normalizeNotification(data)
            
            // 调试日志（生产环境可移除）
            if (process.env.NODE_ENV !== 'production') {
              console.debug('[store] WebSocket消息:', payload)
              console.debug('[store] 规范化后的数据ID:', normalizedData.id)
            }
            
            // 添加到store
            commit('ADD_NOTIFICATION', normalizedData)
            
            // 派发事件，使用已规范化的数据（包含ID）
            if (typeof window !== 'undefined') {
              try {
                window.dispatchEvent(new CustomEvent('notification:received', { detail: normalizedData }))
              } catch (err) {
                console.warn('派发通知事件失败:', err)
              }
            }
          }
        })

        switcher.on('error', (error) => {
          console.error('通知连接出现错误:', error)
          commit('SET_WEBSOCKET_CONNECTED', false)
        })

        switcher.__listenersBound = true
      }

      try {
        const result = await switcher.switchMethod('websocket', resolvedUserId)
        // 检查连接结果，如果有错误但不抛出，而是静默处理
        if (result && result.error) {
          console.warn('WebSocket连接遇到问题，将静默重连:', result.error)
          commit('SET_WEBSOCKET_CONNECTED', false)
        } else {
          commit('SET_WEBSOCKET_CONNECTED', true)
          commit('SET_WEBSOCKET_MANAGER', switcher.webSocketManager || null)
        }
      } catch (error) {
        console.warn('WebSocket连接失败，将静默重连:', error)
        commit('SET_WEBSOCKET_CONNECTED', false)
        // 不抛出错误，让系统静默重连
      }

      if (!state.isInitialized || force) {
        try {
          await Promise.all([
            dispatch('loadNotifications'),
            dispatch('loadTodos'),
            dispatch('loadUnreadCount'),
            dispatch('loadStats')
          ])
        } catch (error) {
          console.error('初始化数据加载失败:', error)
        } finally {
          commit('SET_INITIALIZED', true)
        }
      }
    } catch (error) {
      console.warn('初始化通知中心遇到问题，将静默重连:', error)
      // 不抛出错误，让系统静默重连
    }
  },

  // 加载通知列表
  async loadNotifications({ commit, state }, params = {}) {
    commit('SET_LOADING', true)
    try {
      // console.log('开始加载通知列表，参数:', { page: 1, size: state.pagination.size, ...params })
      const response = await getNotificationList({
        page: 1,
        size: state.pagination.size,
        ...params
      })
      
      // console.log('通知列表API响应:', response)
      // console.log('通知列表API响应数据结构:', JSON.stringify(response.data, null, 2))
      
      if (response && response.data) {
        // 检查API响应结构
        let notifications, total, page, size
        
        if (response.data.code === '0' && response.data.data) {
          // 新格式: {code: "0", data: {total, list, page, size}}
          notifications = response.data.data.list || []
          total = response.data.data.total || 0
          page = response.data.data.page || 1
          size = response.data.data.size || 20
        } else if (response.data.list) {
          // 旧格式: {list, total, page, size}
          notifications = response.data.list || []
          total = response.data.total || 0
          page = response.data.page || 1
          size = response.data.size || 20
        } else if (Array.isArray(response.data)) {
          // 直接数组格式
          notifications = response.data
          total = response.data.length
          page = 1
          size = 20
        } else {
          console.warn('未知的API响应格式:', response.data)
          notifications = []
          total = 0
          page = 1
          size = 20
        }
        
        // console.log('解析的通知数据:', { notifications, total, page, size })
        
        commit('SET_NOTIFICATIONS', {
          data: notifications,
          total: total
        })
        commit('UPDATE_PAGINATION', {
          page: page,
          size: size,
          total: total
        })
      } else {
        console.warn('通知列表API响应格式不正确:', response)
      }
    } catch (error) {
      console.error('加载通知列表失败:', error)
      // 可以在这里添加错误提示
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // 加载更多通知
  async loadMoreNotifications({ commit, state }) {
    if (state.loading) return
    
    const nextPage = Math.floor(state.notifications.length / state.pagination.size) + 1
    const hasMore = state.notifications.length < state.pagination.total
    
    if (!hasMore) return
    
    // console.log(`加载更多通知 - 当前页: ${nextPage}, 当前数量: ${state.notifications.length}, 总数: ${state.pagination.total}`)
    
    commit('SET_LOADING', true)
    try {
      const response = await getNotificationList({
        page: nextPage,
        size: state.pagination.size
      })
      
      if (response && response.data) {
        let newNotifications = []
        
        // 处理不同的API响应格式
        if (response.data.code === '0' && response.data.data) {
          newNotifications = response.data.data.list || []
        } else if (response.data.list) {
          newNotifications = response.data.list || []
        } else if (Array.isArray(response.data)) {
          newNotifications = response.data
        }
        
        // console.log(`获取到 ${newNotifications.length} 条新通知`)
        
        if (newNotifications.length > 0) {
          commit('APPEND_NOTIFICATIONS', newNotifications)
          // console.log(`追加后总通知数: ${state.notifications.length + newNotifications.length}`)
        }
      }
    } catch (error) {
      console.error('加载更多通知失败:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // 加载待办列表
  async loadTodos({ commit, state }, params = {}) {
    commit('SET_LOADING', true)
    try {
      // console.log('开始加载待办列表，参数:', { page: 1, size: state.pagination.size, ...params })
      const response = await getTodoList({
        page: 1,
        size: state.pagination.size,
        ...params
      })
      
      // console.log('待办列表API响应:', response)
      // console.log('待办列表API响应数据结构:', JSON.stringify(response.data, null, 2))
      
      if (response && response.data) {
        // 检查API响应结构
        let todos, total, page, size
        
        if (response.data.code === '0' && response.data.data) {
          // 新格式: {code: "0", data: {total, list, page, size}}
          todos = response.data.data.list || []
          total = response.data.data.total || 0
          page = response.data.data.page || 1
          size = response.data.data.size || 20
        } else if (response.data.list) {
          // 旧格式: {list, total, page, size}
          todos = response.data.list || []
          total = response.data.total || 0
          page = response.data.page || 1
          size = response.data.size || 20
        } else if (Array.isArray(response.data)) {
          // 直接数组格式
          todos = response.data
          total = response.data.length
          page = 1
          size = 20
        } else {
          console.warn('未知的API响应格式:', response.data)
          todos = []
          total = 0
          page = 1
          size = 20
        }
        
        // console.log('解析的待办数据:', { todos, total, page, size })
        
        commit('SET_TODOS', {
          data: todos
        })
        commit('UPDATE_PAGINATION', {
          page: page,
          size: size,
          total: total
        })
      } else {
        console.warn('待办列表API响应格式不正确:', response)
      }
    } catch (error) {
      console.error('加载待办列表失败:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // 加载更多待办
  async loadMoreTodos({ commit, state }) {
    if (state.loading) return
    
    const nextPage = Math.floor(state.todos.length / state.pagination.size) + 1
    const hasMore = state.todos.length < state.pagination.total
    
    if (!hasMore) return
    
    // console.log(`加载更多待办 - 当前页: ${nextPage}, 当前数量: ${state.todos.length}, 总数: ${state.pagination.total}`)
    
    commit('SET_LOADING', true)
    try {
      const response = await getTodoList({
        page: nextPage,
        size: state.pagination.size
      })
      
      if (response && response.data) {
        let newTodos = []
        
        // 处理不同的API响应格式
        if (response.data.code === '0' && response.data.data) {
          newTodos = response.data.data.list || []
        } else if (response.data.list) {
          newTodos = response.data.list || []
        } else if (Array.isArray(response.data)) {
          newTodos = response.data
        }
        
        // console.log(`获取到 ${newTodos.length} 条新待办`)
        
        if (newTodos.length > 0) {
          commit('APPEND_TODOS', newTodos)
          // console.log(`追加后总待办数: ${state.todos.length + newTodos.length}`)
        }
      }
    } catch (error) {
      console.error('加载更多待办失败:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // 加载未读数量
  async loadUnreadCount({ commit }) {
    try {
      const response = await getUnreadCount()
      // console.log('未读数量API响应:', response)
      
      if (response && response.data) {
        let count = 0
        
        if (response.data.code === '0' && response.data.data) {
          // 新格式: {code: "0", data: {count: 6}}
          count = response.data.data.count || 0
        } else if (typeof response.data.count === 'number') {
          // 旧格式: {count: 6}
          count = response.data.count
        } else if (typeof response.data === 'number') {
          // 直接数字格式
          count = response.data
        }
        
        // console.log('解析的未读数量:', count)
        commit('SET_UNREAD_COUNT', count)
      }
    } catch (error) {
      console.error('加载未读数量失败:', error)
    }
  },

  // 加载统计信息
  async loadStats({ commit }) {
    try {
      const response = await getNotificationStats()
      if (response && response.data) {
        commit('SET_STATS', response.data)
      }
    } catch (error) {
      console.error('加载统计信息失败:', error)
    }
  },

  // 标记为已读
  async markAsRead({ commit, state }, notificationId) {
    if (!notificationId) {
      console.warn('markAsRead: notificationId 为空，跳过请求')
      return
    }
    try {
      const target = Array.isArray(state.notifications) ? state.notifications.find(n => n.id === notificationId) : null
      const serverId = target && (target.__serverId || target.notificationId)
      await markAsRead(serverId || notificationId)
      commit('MARK_AS_READ', notificationId)
    } catch (error) {
      console.error('标记已读失败:', error)
      throw error
    }
  },

  // 全部标记为已读
  async markAllAsRead({ commit }) {
    try {
      await markAllAsRead()
      commit('MARK_ALL_AS_READ')
    } catch (error) {
      console.error('全部标记已读失败:', error)
      throw error
    }
  },

  // 处理待办事项
  async handleTodo({ dispatch }, { todoId, action, data }) {
    try {
      await handleTodo(todoId, action, data)
      // 重新加载待办列表
      await dispatch('loadTodos')
    } catch (error) {
      console.error('处理待办失败:', error)
      throw error
    }
  },

  // 删除通知
  async deleteNotification({ commit }, notificationId) {
    try {
      await deleteNotification(notificationId)
      commit('REMOVE_NOTIFICATION', notificationId)
    } catch (error) {
      console.error('删除通知失败:', error)
      throw error
    }
  },

  // 批量删除通知
  async batchDeleteNotifications({ commit }, notificationIds) {
    try {
      await batchDeleteNotifications(notificationIds)
      commit('BATCH_REMOVE_NOTIFICATIONS', notificationIds)
    } catch (error) {
      console.error('批量删除通知失败:', error)
      throw error
    }
  },

  // 搜索通知
  async searchNotifications({ commit, dispatch }, keyword) {
    commit('SET_SEARCH_KEYWORD', keyword)
    await dispatch('loadNotifications')
  },

  // 设置过滤条件
  async setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters)
    await dispatch('loadNotifications')
  },

  // 更新分页
  async updatePagination({ commit, dispatch }, pagination) {
    commit('UPDATE_PAGINATION', pagination)
    await dispatch('loadNotifications')
  },

  // 刷新数据
  async refresh({ dispatch }) {
    await Promise.all([
      dispatch('loadNotifications'),
      dispatch('loadTodos'),
      dispatch('loadUnreadCount'),
      dispatch('loadStats')
    ])
  },

  // 断开连接
  disconnect({ commit, state }) {
    if (state.notificationSwitcher) {
      state.notificationSwitcher.disconnect()
    } else if (state.websocketManager) {
      state.websocketManager.disconnect()
    }
    commit('SET_WEBSOCKET_CONNECTED', false)
    commit('SET_WEBSOCKET_MANAGER', null)
  },

  // 重连
  async reconnect({ state, dispatch, rootState }) {
    const userId = state.currentUserId || resolveUserId(rootState)
    const baseUrl = state.websocketBaseUrl || resolveNotificationBaseUrl()
    if (userId) {
      await dispatch('initNotificationCenter', { userId, baseUrl, force: true })
    }
  },

  // 清空通知
  clearNotifications({ commit }) {
    commit('CLEAR_NOTIFICATIONS')
  }
}

const getters = {
  // 未读通知
  unreadNotifications: state => state.notifications.filter(n => !n.read),
  
  // 已读通知
  readNotifications: state => state.notifications.filter(n => n.read),
  
  // 按类型过滤的通知
  filteredNotifications: state => {
    let filtered = state.notifications
    
    // 按类型过滤
    if (state.filters.type !== 'all') {
      filtered = filtered.filter(n => n.type === state.filters.type)
    }
    
    // 按状态过滤
    if (state.filters.status === 'read') {
      filtered = filtered.filter(n => n.read)
    } else if (state.filters.status === 'unread') {
      filtered = filtered.filter(n => !n.read)
    }
    
    // 按关键词搜索
    if (state.searchKeyword) {
      const keyword = state.searchKeyword.toLowerCase()
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(keyword) ||
        n.content.toLowerCase().includes(keyword)
      )
    }
    
    return filtered
  },
  
  // 高优先级待办
  highPriorityTodos: state => state.todos.filter(t => t.priority === 'HIGH'),
  
  // 待审批的待办
  pendingTodos: state => state.todos.filter(t => t.status === 'PENDING'),
  
  // 连接状态文本
  connectionStatusText: state => {
    if (state.websocketConnected) return '已连接'
    return '未连接'
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
